import { Avatar } from "@/components/buttons/Avatar";
import { ProcessStatusIcon } from "@/components/status/ProcessStatusTag";
import { getAnalysisCollectionDeleteConfirm } from "@/tools/aggregate/common/AnalysisCollectionDeleteConfirm";
import { AnalysisCollectionEditModal, type AnalysisCollectionEditValues } from "@/tools/aggregate/common/AnalysisCollectionEditModal";
import styles from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobInformation.module.scss";
import { formatRelativeDate, getDaysAgo } from "@/utils/datetime";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { useKeys } from "@/utils/hooks/useKeys";
import { withCommas } from "@/utils/number";
import { DeleteOutlined, EditOutlined, LockFilled, MoreOutlined, StopOutlined } from "@ant-design/icons";
import { Button, Dropdown, MenuProps, Modal, Tag, Tooltip } from "antd";
import classnames from "classnames";
import dynamic from "next/dynamic";
import { useState } from "react";
import type { SeveritiesJobSchema } from "../types";

// streamsaver only loads in the browser, so it can't be imported during the build.
const SeveritiesDownloadButton = dynamic(() => import("./SeveritiesDownloadButton"), {
  ssr: false
});
export type SeveritiesEditValues = AnalysisCollectionEditValues<SeveritiesJobSchema>;
interface SeveritiesJobInformationProps {
  schema: SeveritiesJobSchema;
  onDelete: () => void;
  onEdit: (values: SeveritiesEditValues) => Promise<void>;
}
export const SeveritiesJobInformation = ({
  schema,
  onDelete,
  onEdit
}: SeveritiesJobInformationProps) => {
  const {
    totalCount,
    processedCount,
    errorCount,
    unprocessedCount
  } = schema.assets;
  const startTime = new Date(schema.startTime);
  const isComplete = schema.status === "completed" || schema.status === "completed_with_errors";
  const [modal, modalContext] = Modal.useModal();
  const [showEditModal, setShowEditModal] = useState(false);
  const isOwner = useIsOwner();
  const [key] = useKeys();
  const onDeleteJob = async (action: "delete" | "cancel") => {
    const confirmed = await modal.confirm(getAnalysisCollectionDeleteConfirm(schema.name, action));
    if (confirmed) onDelete();
  };
  const threeDotMenuItems: MenuProps["items"] = [{
    key: "edit-severities",
    label: "Edit",
    icon: <EditOutlined />,
    onClick: () => setShowEditModal(true)
  }, {
    key: "delete-severities",
    label: "Delete",
    icon: <DeleteOutlined />,
    onClick: () => onDeleteJob("delete")
  }];
  return <div className="flex w-full items-center justify-between gap-4" data-sentry-component="SeveritiesJobInformation" data-sentry-source-file="SeveritiesJobInformation.tsx">
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-medium text-gray-900">
          <div className="flex h-4 w-4 items-center justify-center overflow-hidden">
            <ProcessStatusIcon status={schema.status} data-sentry-element="ProcessStatusIcon" data-sentry-source-file="SeveritiesJobInformation.tsx" />
          </div>
          <span>{schema.name}</span>
          <SeveritiesJobStatus schema={schema} data-sentry-element="SeveritiesJobStatus" data-sentry-source-file="SeveritiesJobInformation.tsx" />
        </div>

        <div className={classnames(styles.smallText, "mt-0.5 flex items-center text-xs leading-none text-gray-500")}>
          <span>
            <Tooltip placement="top" title={formatRelativeDate(startTime)} data-sentry-element="Tooltip" data-sentry-source-file="SeveritiesJobInformation.tsx">
              <span>{getDaysAgo(startTime)}</span>
            </Tooltip>
          </span>

          <span>
            <div className="flex items-center space-x-1">
              <Avatar name={schema.email} size={14} data-sentry-element="Avatar" data-sentry-source-file="SeveritiesJobInformation.tsx" />
              <span className="ml-1">{schema.email}</span>
            </div>
          </span>

          {schema.isPrivate ? <span>
              <Tag icon={<LockFilled />} className="m-0 text-gray-500">
                Private
              </Tag>
            </span> : <span>{schema.groupName}</span>}

          <span>
            {withCommas(totalCount)} assets
            {errorCount > 0 && <span className="pl-1">({withCommas(errorCount)} failed)</span>}
          </span>

          {schema.status === "processing" && totalCount - processedCount - errorCount > 0 && <span>{withCommas(totalCount - processedCount - errorCount)} processing</span>}

          {schema.status === "uploading" && <span>{withCommas(totalCount - unprocessedCount)} uploaded</span>}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {isComplete ? <SeveritiesDownloadButton schema={schema} /> : <Button icon={<StopOutlined />} style={{
        fontWeight: "inherit"
      }} onClick={() => onDeleteJob("cancel")}>
            Cancel
          </Button>}
        <Dropdown trigger={["click"]} disabled={!isComplete} menu={{
        items: threeDotMenuItems
      }} styles={{
        root: {
          width: 160
        }
      }} data-sentry-element="Dropdown" data-sentry-source-file="SeveritiesJobInformation.tsx">
          <Button icon={<MoreOutlined />} style={{
          fontWeight: "inherit"
        }} type="text" data-sentry-element="Button" data-sentry-source-file="SeveritiesJobInformation.tsx" />
        </Dropdown>
      </div>
      <AnalysisCollectionEditModal schema={schema} isOwner={isOwner || false} open={showEditModal} onCancel={() => setShowEditModal(false)} onOk={onEdit} hasOrgKeys={!!key.orgSecret} data-sentry-element="AnalysisCollectionEditModal" data-sentry-source-file="SeveritiesJobInformation.tsx" />
      {modalContext}
    </div>;
};
const SeveritiesJobStatus = ({
  schema
}: {
  schema: SeveritiesJobSchema;
}) => {
  const {
    totalCount,
    processedCount,
    errorCount,
    unprocessedCount
  } = schema.assets;
  switch (schema.status) {
    case "processing":
      {
        const percent = totalCount ? (processedCount + errorCount) / totalCount * 100 : 0;
        return <Tag color="blue">{percent.toFixed(1)}% PROCESSED</Tag>;
      }
    case "uploading":
      {
        const percent = totalCount ? (totalCount - unprocessedCount) / totalCount * 100 : 0;
        return <Tag color="orange">{percent.toFixed(1)}% UPLOADED</Tag>;
      }
    default:
      return null;
  }
};